import React, {useEffect, useRef, useState} from "react";
import {useTheme} from "connectui/theme/ThemeProvider";
import {buildStyles} from "connectui/component/buildStyles";
import SvgIcon from "connectui/component/display/SvgIcon";
import ICONS from "connectui/icons/iconMap";
import ChipRow from "connectui/component/display/chip/ChipRow";
import ChipText from "connectui/component/display/chip/ChipText";
import XChipBase from "connectui/component/display/chip/base/XChipBase";
import ChipBase from "connectui/component/display/chip/base/ChipBase";
import Skeleton from "connectui/component/display/Skeleton";
import FlexContainer from "connectui/component/layout/FlexContainer";
import ChipIconText from "connectui/component/display/chip/ChipIconText";
import Tooltip from "connectui/component/display/Tooltip";
import {useHistory} from "react-router-dom";
import {useTranslation} from "react-i18next";
import GuestSelectorMenu from "../../../shared/context/GuestSelectorMenu";
import Text from "connectui/component/display/Text";

const guestTypeIcons = {
  guest: ICONS.user,
  owner: ICONS["home-owner"],
  maintenance: ICONS.tool,
  cleaner: ICONS.clean,
};

const useStyles = (theme) =>
  buildStyles({
    header: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
    },
    container: {
      width: "100%",
    },
    chipRow: {
      maxWidth: "70%",
    },
  });

export default function GuestDetailsFollowers({guest, updateGuest}) {
  const {theme, isMobile} = useTheme();
  const classes = useStyles(theme);
  const followersIds = guest?.followers || [];
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedIds, setSelectedIds] = useState(followersIds);
  const initialSelected = useRef(followersIds);
  const [followersMetadata, setFollowersMetadata] = useState([]);
  const history = useHistory();
  const {t} = useTranslation();

  useEffect(() => {
    if (guest?.followers_metadata) {
      setFollowersMetadata(guest.followers_metadata);
    }
  }, [guest?.followers_metadata]);

  useEffect(() => {
    if (guest?.followers) {
      setSelectedIds(followersIds);
      initialSelected.current = followersIds;
    }
  }, [guest?.followers, followersIds]);

  const handleUpdate = () => {
    const initial = initialSelected.current;
    const added = selectedIds.filter((id) => !initial.includes(id));
    const removed = initial.filter((id) => !selectedIds.includes(id));

    setAnchorEl(null);

    if (removed && removed.length > 0) {
      updateGuest({guest_ids: removed, add: false});
    }
    if (added && added.length > 0) {
      updateGuest({guest_ids: added, add: true});
    }
  };

  const handleOpenMenu = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleGuestClick = (guest_id) => {
    const searchParams = new URLSearchParams(window.location.search);
    const viewId = searchParams.get("view_id");
    history.push(
      `/admin/messages/${isMobile ? "chat/" : ""}${guest_id}${
        viewId !== null ? `?view_id=${viewId}` : ""
      }`,
    );
  };

  const handleGuestSelection = (guest_id, isSelected, guestItem) => {
    if (isSelected) {
      setSelectedIds((prev) => prev.filter((id) => id !== guest_id));
      setFollowersMetadata((prev) =>
        prev.filter((f) => f.guest_id !== guest_id),
      );
    } else {
      setSelectedIds((prev) =>
        prev.includes(guest_id) ? prev : [...prev, guest_id],
      );
      setFollowersMetadata((prev) => {
        if (prev.some((f) => f.guest_id === guest_id)) return prev;
        return [
          ...prev,
          {
            guest_id: guest_id,
            name: guestItem.name,
            type: guestItem.guest_type,
          },
        ];
      });
    }
  };

  return (
    <FlexContainer
      flexDirection="row"
      alignItems="flex-start"
      justifyContent="space-between"
      className={classes.container}
    >
      <FlexContainer
        flexDirection="row"
        alignItems="center"
        className={classes.header}
        gap={theme.spacing(1)}
      >
        <SvgIcon
          component={ICONS.users}
          sx={{
            color: theme.colors.textSecondary,
            width: "14px",
            height: "14px",
          }}
        />
        <Text variant="textSM" color="textSecondary" weight="medium">
          {t("inbox.guest-details.followers.label")}
        </Text>
      </FlexContainer>
      {guest === undefined ? (
        <Skeleton width="50%" />
      ) : (
        <>
          <ChipRow
            wrap
            sx={{maxWidth: "70%"}}
            align="right"
            chips={[
              ...selectedIds.map((g_id) => {
                const selectedFollower = followersMetadata.find(
                  (f) => f.guest_id === g_id,
                );
                if (!selectedFollower?.name)
                  return {
                    Component: Skeleton,
                    width: "60px",
                    height: "22px",
                    style: {borderRadius: "30px"},
                  };
                return {
                  Component: ({children, ...props}) => (
                    <Tooltip
                      colors={{bg: "gray900", text: "white"}}
                      tooltipContent={
                        <Text variant="textXS" color="white">
                          {t(
                            "inbox.guest-details.followers.view-conversation",
                            {name: selectedFollower?.name},
                          )}
                        </Text>
                      }
                    >
                      <ChipIconText {...props}>{children}</ChipIconText>
                    </Tooltip>
                  ),
                  text: selectedFollower?.name,
                  icon: (
                    <SvgIcon
                      component={guestTypeIcons[selectedFollower?.type]}
                      sx={{
                        width: "16px",
                        height: "16px",
                        padding: theme.spacing(0.5),
                        backgroundColor: theme.colors.white,
                        borderRadius: "50%",
                      }}
                    />
                  ),
                  typographyVariant: "textXS",
                  color: "gray700",
                  chipBase: XChipBase,
                  XColor: theme.colors.gray500,
                  onClick: (e) => {
                    e.stopPropagation();
                    handleGuestClick(g_id);
                  },
                  onXClick: (e) => {
                    e.stopPropagation();
                    setSelectedIds((prev) => prev.filter((id) => id !== g_id));
                    setFollowersMetadata((prev) =>
                      prev.filter((f) => f.guest_id !== g_id),
                    );
                    updateGuest({guest_ids: [g_id], add: false});
                  },
                  sx: {
                    backgroundColor: theme.colors.gray100,
                    color: theme.colors.gray700,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: theme.colors.gray200,
                    },
                  },
                };
              }),
              {
                Component: ChipText,
                text: (
                  <SvgIcon
                    component={ICONS.plus}
                    sx={{width: "14px", height: "14px", padding: "1px"}}
                  />
                ),
                typographyVariant: "textXS",
                color: "gray700",
                chipBase: ChipBase,
                onClick: handleOpenMenu,
                sx: {
                  cursor: "pointer",
                  backgroundColor: theme.colors.gray100,
                  color: theme.colors.gray700,
                  padding: theme.spacing(1),
                  "&:hover": {
                    backgroundColor: theme.colors.gray200,
                  },
                },
              },
            ]}
          />
          <GuestSelectorMenu
            open={!!anchorEl}
            onClose={handleUpdate}
            anchorEl={anchorEl}
            selectedIds={selectedIds}
            onGuestSelection={handleGuestSelection}
            ensoKey={guest?.enso_key}
            guestFilters={[
              {
                path: "guest_type",
                value: ["owner", "maintenance", "cleaner"],
              },
              {path: "guest_tags", operator: "!=", value: "archived"},
            ]}
          />
        </>
      )}
    </FlexContainer>
  );
}
